<template>
  <div class="space-y-3">
    <!-- Gradient Preview Bar -->
    <div 
      class="relative h-8 rounded-lg cursor-pointer overflow-hidden"
      :style="{ background: gradientCss }"
      @click="handleBarClick"
    >
      <!-- Color Stops -->
      <div
        v-for="stop in gradient.stops"
        :key="stop.id"
        class="absolute top-0 bottom-0 w-1 -ml-0.5 cursor-grab active:cursor-grabbing"
        :style="{ left: `${stop.position * 100}%` }"
        @mousedown.stop="(e) => startDrag(e, stop)"
        @touchstart.stop="(e) => startDrag(e, stop)"
        @click.stop
        @dblclick.stop="() => handleStopDoubleClick(stop)"
      >
        <!-- Stop Handle -->
        <div 
          class="absolute -top-1 -left-1.5 w-4 h-4 rounded-full border-2 border-white shadow-md"
          :style="{ backgroundColor: stopToCss(stop) }"
          :class="selectedStopId === stop.id ? 'ring-2 ring-primary ring-offset-1' : ''"
        />
        <!-- Stop Position Indicator -->
        <div 
          class="absolute -bottom-1 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[4px] border-r-[4px] border-t-[6px] border-l-transparent border-r-transparent border-t-white"
        />
      </div>
    </div>

    <!-- Selected Stop Info (always reserve space to prevent layout shift) -->
    <div class="flex items-center gap-2 p-2 border rounded-lg min-h-[42px]" :class="{ 'opacity-50': !selectedStop }">
      <template v-if="selectedStop">
        <div 
          class="w-6 h-6 rounded-md border shadow-inner flex-shrink-0"
          :style="{ backgroundColor: stopToCss(selectedStop) }"
        />
        <span class="text-xs text-muted-foreground flex-1">
          Position: {{ Math.round(selectedStop.position * 100) }}%
        </span>
        <span class="text-xs text-muted-foreground">
          Edit on wheel
        </span>
        <Button
          v-if="gradient.stops.length > 2"
          variant="ghost"
          size="sm"
          @click="removeSelectedStop"
          class="h-6 w-6 p-0 text-destructive hover:text-destructive"
        >
          <Trash2 class="h-3 w-3" />
        </Button>
      </template>
      <template v-else>
        <span class="text-xs text-muted-foreground">Click a stop to select</span>
      </template>
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { Button } from '@/components/ui/button'
import { Trash2 } from 'lucide-vue-next'
import { type Gradient, type GradientStop, hueHsbToCss } from '@/types/wledEffect'

// Helper to convert hue/sat to XY coordinates
function hueToXY(hue: number, saturation: number): { x: number, y: number } {
  const angle = (hue / 65535) * 2 * Math.PI
  const radius = saturation / 254
  return {
    x: Math.cos(angle) * radius,
    y: Math.sin(angle) * radius
  }
}

// Helper to convert XY back to hue/sat
function xyToHue(x: number, y: number): { hue: number, saturation: number } {
  const radius = Math.sqrt(x * x + y * y)
  let angle = Math.atan2(y, x)
  if (angle < 0) angle += 2 * Math.PI
  
  return {
    hue: Math.round((angle / (2 * Math.PI)) * 65535),
    saturation: Math.round(Math.min(radius, 1) * 254)
  }
}

const props = defineProps<{
  gradient: Gradient
}>()

const emit = defineEmits<{
  'update:stop-position': [stopId: string, position: number]
  'update:stop-color': [stopId: string, color: GradientStop['color']]
  'add-stop': [position: number, color: GradientStop['color']]
  'remove-stop': [stopId: string]
}>()

// Selected stop for editing
const selectedStopId = ref<string | null>(null)

// Get selected stop
const selectedStop = computed(() => {
  if (!selectedStopId.value) return null
  return props.gradient.stops.find(s => s.id === selectedStopId.value) || null
})

// Clear selection when gradient changes (e.g., reset)
watch(() => props.gradient.stops.map(s => s.id).join(','), () => {
  // If selected stop no longer exists, clear selection
  if (selectedStopId.value && !props.gradient.stops.find(s => s.id === selectedStopId.value)) {
    selectedStopId.value = null
  }
})

// Convert stop to CSS color
function stopToCss(stop: GradientStop): string {
  return hueHsbToCss(stop.color.hue, stop.color.saturation, stop.color.brightness)
}

// Generate CSS gradient
const gradientCss = computed(() => {
  const stops = [...props.gradient.stops].sort((a, b) => a.position - b.position)
  const cssStops = stops.map(s => `${stopToCss(s)} ${s.position * 100}%`)
  return `linear-gradient(to right, ${cssStops.join(', ')})`
})

// Handle click on gradient bar to add stop
function handleBarClick(e: MouseEvent) {
  // Don't add a stop if we just finished dragging
  if (wasDragging) {
    wasDragging = false
    return
  }
  
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
  const position = (e.clientX - rect.left) / rect.width
  
  // Find the two stops this position is between
  const sortedStops = [...props.gradient.stops].sort((a, b) => a.position - b.position)
  let leftStop = sortedStops[0]
  let rightStop = sortedStops[sortedStops.length - 1]
  
  for (let i = 0; i < sortedStops.length - 1; i++) {
    if (position >= sortedStops[i].position && position <= sortedStops[i + 1].position) {
      leftStop = sortedStops[i]
      rightStop = sortedStops[i + 1]
      break
    }
  }
  
  // Interpolate in XY space for smooth color blending
  const t = rightStop.position === leftStop.position 
    ? 0 
    : (position - leftStop.position) / (rightStop.position - leftStop.position)
  
  // Convert both stops to XY coordinates
  const leftXY = hueToXY(leftStop.color.hue, leftStop.color.saturation)
  const rightXY = hueToXY(rightStop.color.hue, rightStop.color.saturation)
  
  // Interpolate in XY space
  const interpX = leftXY.x + (rightXY.x - leftXY.x) * t
  const interpY = leftXY.y + (rightXY.y - leftXY.y) * t
  
  // Convert back to hue/sat
  const interpHueSat = xyToHue(interpX, interpY)
  
  // Interpolate brightness linearly
  const interpBrightness = Math.round(
    leftStop.color.brightness + (rightStop.color.brightness - leftStop.color.brightness) * t
  )
  
  const interpolatedColor = {
    hue: interpHueSat.hue,
    saturation: interpHueSat.saturation,
    brightness: interpBrightness
  }
  
  emit('add-stop', position, interpolatedColor)
}

// Remove selected stop
function removeSelectedStop() {
  if (!selectedStopId.value) return
  if (props.gradient.stops.length <= 2) return
  emit('remove-stop', selectedStopId.value)
  selectedStopId.value = null
}

// Handle double-click on stop to remove it
function handleStopDoubleClick(stop: GradientStop) {
  if (props.gradient.stops.length <= 2) return
  emit('remove-stop', stop.id)
  if (selectedStopId.value === stop.id) {
    selectedStopId.value = null
  }
}

// Drag handling
let isDragging = false
let wasDragging = false  // Track if we were dragging to prevent click from adding stop
let dragStopId: string | null = null

function startDrag(e: MouseEvent | TouchEvent, stop: GradientStop) {
  e.preventDefault()
  isDragging = true
  wasDragging = false
  dragStopId = stop.id
  selectedStopId.value = stop.id
  
  document.addEventListener('mousemove', handleDrag)
  document.addEventListener('mouseup', endDrag)
  document.addEventListener('touchmove', handleDrag)
  document.addEventListener('touchend', endDrag)
}

function handleDrag(e: MouseEvent | TouchEvent) {
  if (!isDragging || !dragStopId) return
  
  // Mark that we actually dragged (moved the mouse)
  wasDragging = true
  
  const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
  const bar = document.querySelector('[style*="background: linear-gradient"]') as HTMLElement
  if (!bar) return
  
  const rect = bar.getBoundingClientRect()
  let position = (clientX - rect.left) / rect.width
  position = Math.max(0, Math.min(1, position))
  
  emit('update:stop-position', dragStopId, position)
}

function endDrag() {
  isDragging = false
  dragStopId = null
  // wasDragging is NOT reset here - it will be reset in handleBarClick
  // This allows the click handler to know we just finished dragging
  
  document.removeEventListener('mousemove', handleDrag)
  document.removeEventListener('mouseup', endDrag)
  document.removeEventListener('touchmove', handleDrag)
  document.removeEventListener('touchend', endDrag)
}
</script>
